// Telemetry
/** Redaction marker used to replace scrubbed values */ const Redacted = "[REDACTED]";
/**
 * Matches http/https URLs in text. Greedy match of all non-whitespace/non-quote characters.
 * Trailing punctuation is handled by {@link scrubUrlQueryParams} to avoid losing delimiters.
 */ const UrlRegex = /https?:\/\/[^\s'"]+/g;
/**
 * Scrubs query parameters from URLs in a string to remove EUII.
 * Designed to be fast and safe for high-frequency calling on all telemetry data.
 *
 * Strips query parameters from URLs (which may contain displayName, userUpn, etc.)
 * while preserving the path, :line:col suffix for callstack debugging, and any
 * trailing punctuation that was adjacent to the URL.
 * Privacy Guard provides additional PII detection on all telemetry events sent to OneDS.
 *
 * @param input String to scrub (callstack, error message, log text, etc.)
 * @returns Scrubbed string with URL query params replaced by redaction markers
 */ export function scrubUrls(input) {
    if (!input || typeof input !== "string") {
        return input ?? "";
    }
    return input.replace(UrlRegex, scrubUrlQueryParams);
}
/**
 * Strips query parameters from a URL while preserving the path and any suffix.
 * Recognizes two suffix patterns to preserve:
 * - Callstack :line:col) format (e.g., ":2:208920)")
 * - Trailing punctuation not part of the URL (e.g., "),", ";")
 *
 * @example
 * // Callstack URL:
 * // "https://host/sw.js?displayName=John%20Doe&userUpn=john%40contoso.com:2:208920)"
 * // becomes: "https://host/sw.js?[REDACTED]:2:208920)"
 *
 * @example
 * // URL followed by punctuation:
 * // "https://example.com?x=y),"
 * // becomes: "https://example.com?[REDACTED]),"
 *
 * @param url Matched URL string to scrub
 * @returns URL with query params replaced by redaction marker, suffix preserved
 */ function scrubUrlQueryParams(url) {
    const queryIndex = url.indexOf("?");
    if (queryIndex === -1) {
        return url;
    }
    const basePath = url.substring(0, queryIndex);
    const rest = url.substring(queryIndex + 1);
    // Preserve suffix at the end of the query string. Two cases:
    // 1. Callstack :line:col with optional trailing punctuation (e.g., ":2:208920)")
    // 2. Just trailing punctuation (e.g., ")", "),", ";")
    // Using alternation so at least one branch must match non-empty.
    const suffixMatch = rest.match(/:(\d+:\d+)[)\],;.]*$/) || rest.match(/[)\],;.]+$/);
    const suffix = suffixMatch?.[0] ?? "";
    return basePath + "?" + Redacted + suffix;
}
/**
 * Scrubs URL query params from all string values in a record. Keys are preserved unchanged.
 * @param record Optional record to scrub
 * @returns New record with scrubbed values, or undefined if input was undefined
 */ export function scrubUrlsInRecord(record) {
    if (!record) {
        return record;
    }
    const result = {};
    for (const key of Object.keys(record)){
        result[key] = scrubUrls(record[key]);
    }
    return result;
}
/**
 * Scrubs URL query params from string DataField values. Non-string fields are passed through unchanged.
 * For use in UTelLogHandler to scrub all telemetry event payloads before they reach OneDS.
 * @param fields Optional array of DataFields to scrub
 * @returns New array with string field values scrubbed, or undefined if input was undefined
 */ export function scrubUrlsInDataFields(fields) {
    if (!fields) {
        return fields;
    }
    return fields.map((field)=>{
        if (typeof field.value === "string") {
            const scrubbed = scrubUrls(field.value);
            if (scrubbed !== field.value) {
                return {
                    ...field,
                    value: scrubbed
                };
            }
        }
        return field;
    });
}
